import { useEffect } from 'react';

import { useCommandPaletteState } from '../commandPaletteState';
import {
    selectCanAdjustBet,
    selectCanOpen,
    useTradingStore,
} from '../store';
import type { PositionType } from '../types';

interface UseKeyboardShortcutsParams {
    onOpen: (type: PositionType, amount: number) => void;
}

/**
 * Overlay surfaces whose own keyboard handling must win over the global
 * trading shortcuts: any Radix dialog/select/combobox subtree, plus a Vaul
 * drawer (which renders `[data-vaul-drawer]` only while mounted/open).
 */
const OVERLAY_TARGET_SELECTOR =
    '[role="dialog"],[role="listbox"],[role="combobox"],[role="menu"]';
const OPEN_OVERLAY_SELECTOR =
    '[data-state="open"][role="dialog"], [data-vaul-drawer]';

/**
 * Whether a global trading shortcut must stand down for this event.
 *
 * Evaluated per-event on purpose — nothing here may be cached, since a
 * dialog can open or close between two keystrokes.
 *
 * Without these bails the capture-phase listener hijacks keys that belong to
 * whatever is on top: typing "l"/"s" in the market picker opened positions,
 * and back when close was bound to `Esc`, an `Esc` with a dialog open executed
 * a real market close AND its `preventDefault()` suppressed Radix's dismiss
 * (DismissableLayer gates `onDismiss` on `!defaultPrevented`).
 */
function isShortcutSuppressed(e: KeyboardEvent): boolean {
    // Someone upstream already claimed this key.
    if (e.defaultPrevented) return true;

    // The ⌘K palette owns the keyboard while it is open (its input is
    // portalled, but arrow/Esc handling is its own).
    if (useCommandPaletteState.getState().open) return true;

    const target = e.target;
    if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement
    )
        return true;
    if (target instanceof HTMLElement && target.isContentEditable) return true;
    if (target instanceof Element && target.closest(OVERLAY_TARGET_SELECTOR))
        return true;

    // Focus can sit on <body> while a modal is up (Radix moves it), so also
    // bail whenever any dialog/drawer is open anywhere in the document.
    return document.querySelector(OPEN_OVERLAY_SELECTOR) !== null;
}

/**
 * Registers a global `keydown` listener for trading keyboard shortcuts.
 *
 * CLOSING HAS NO KEY BINDING (unbound 2026-09-04). It was `c` / `Esc`, and Esc
 * is the key everyone presses to back out of something: the suppression above
 * covers inputs and overlays, but a collapsed trigger editor leaves focus on a
 * plain button, where an Esc meant for "I'm done editing" reached this listener
 * and sent an irreversible market close with no confirmation — the likely path
 * by which a live ETH long was closed at 2521.5 a minute after its take-profit
 * was edited, with its stop still resting 20 points below. Closing now takes
 * the button or the ⌘K palette — both deliberate.
 *
 * Reads `canOpen`, `canAdjustBet`, and `betAmount` from the store inside the
 * event handler so the listener doesn't need to be torn down and re-registered
 * on every state change.
 */
export function useKeyboardShortcuts({
    onOpen,
}: UseKeyboardShortcutsParams): void {
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (isShortcutSuppressed(e)) return;

            const key = e.key.toLowerCase();
            const s = useTradingStore.getState();
            if (!s.shortcutsEnabled) return;
            const canOpen = selectCanOpen(s);
            const canAdjust = selectCanAdjustBet(s);

            if (canOpen) {
                if (key === 'l' || key === 'arrowup') {
                    e.preventDefault();
                    onOpen('LONG', s.betAmount);
                }
                if (key === 's' || key === 'arrowdown') {
                    e.preventDefault();
                    onOpen('SHORT', s.betAmount);
                }
            }

            if (canAdjust) {
                if (key === 'arrowright' || key === '=' || key === '+') {
                    e.preventDefault();
                    s.adjustBet(10);
                }
                if (key === 'arrowleft' || key === '-') {
                    e.preventDefault();
                    s.adjustBet(-10);
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown, true);
        return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [onOpen]);
}
